import { useTranslation } from 'react-i18next';
import sortBy from 'lodash/sortBy';
import { MENU_NAMES } from '@/consts/menu-names.consts';
import { Box } from '@/UI/Box/Box';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import { log } from '@/logger/logger.ts';
import { I18N_KEYS } from '@/language/keys';
import { EllipsisText } from '@/UI/EllipsisText/EllipsisText';
import { type MenuAction } from '@/components/Menu/Actions';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { WorkspaceIcon } from '../WorkspaceIcon';
import { Plan } from './Workspaces.styles';
import type { ActiveWorkspace, BaseWorkspace } from '@/types/auth.types';

const logger = log.getLogger('WorkspacesMenu');

interface WorkspacesMenuProps {
  anchorEl: HTMLElement | null;
  workspaces: BaseWorkspace[];
  activeWorkspace: ActiveWorkspace;
  onSelect: (workspace: BaseWorkspace) => Promise<void>;
  onClose: () => void;
}

export const WorkspacesMenu = ({ anchorEl, workspaces, activeWorkspace, onSelect, onClose }: WorkspacesMenuProps) => {
  const { t } = useTranslation();
  const isFplMigrationEnabled = useFplMigrationFlag();

  const handleSwitchWorkspace = async (workspace: BaseWorkspace) => {
    try {
      await onSelect(workspace);
      onClose();
    } catch (e) {
      logger.error('Error switching workspace', e);
    }
  };

  const renderPlan = (workspace: BaseWorkspace | ActiveWorkspace) => {
    if ('subscription' in workspace && workspace.subscription?.name) {
      return <Plan variant="label-xs-rg">{workspace.subscription?.name}</Plan>;
    }
  };

  const workspaceItems: MenuAction[] = sortBy(workspaces, 'workspaceName').map((workspace) => {
    const isSelected = workspace.workspaceId === activeWorkspace.workspaceId;

    return {
      biEventKey: { menu: MENU_NAMES.WORKSPACES_MENU, action: 'switch_workspace' },
      action: () => (isSelected ? undefined : handleSwitchWorkspace(workspace)),
      withLoading: !isSelected,
      icon: isFplMigrationEnabled ? (
        <Box component="span" sx={{ display: 'inline-flex', ml: '4px' }}>
          <WorkspaceIcon text={workspace.workspaceName} size="xs" />
        </Box>
      ) : (
        <WorkspaceIcon text={workspace.workspaceName} />
      ),
      name: (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', minWidth: '50px' }}>
          <EllipsisText maxWidth="100px" variant="body-sm-rg" color="inherit" sx={{ display: 'inline-block' }}>
            {workspace.workspaceName}
          </EllipsisText>
        </Box>
      ),
      selected: isSelected,
      endContent: isSelected ? undefined : renderPlan(workspace),
      sx: { py: 1 },
    };
  });

  const menuItems: MenuAction[] = [
    {
      type: 'group',
      title: t(I18N_KEYS.MAIN_DASHBOARD.WORKSPACES_MENU.TITLE),
      items: workspaceItems,
    },
  ];

  return (
    <AppContextMenu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      offset={isFplMigrationEnabled ? { left: '16px', top: '-8px' } : { left: '18px', top: '-12px' }}
      open={Boolean(anchorEl)}
      onClose={onClose}
      items={menuItems}
    />
  );
};
